import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Box
} from '@mui/material';

// Configuration des couleurs
const redFeatures = new Set([
  'Tutor-Guided Isam more Sentence Writing',
  'Tutor-guided Grammar Scope and Sequence',
  '2 Week Free Trial'
]);

const blueFeatures = new Set([
  'Vocabulary from the Student’s Reading(s)'
]);

const plans = [
  {
    title: 'Study Plan 1',
    price: { monthly: 12, yearly: 100, save: 40 },
    features: [
      { text: 'Latin Roots', subItems: ['Why study roots?'] },
      'Root Families',
      'Excerpt Reading',
      'Parts of Speech',
      'Synonyms',
      'Definitions',
      'Spelling',
      'Syllables',
      'Pronunciation',
      'Word Games',
      'Prep for: ISEE, CTP, PSAT, SAT, ACT, GRE',
      'Progress Reports'
    ],
    buttons: { 
      monthly: 'Order Plan 1 Monthly', 
      yearly: 'Order Plan 1 Year',
      monthlyLink: '/order/plan1/monthly',
      yearlyLink: '/order/plan1/yearly'
    }
  },
  {
    title: 'Study Plan 2',
    price: { monthly: 35, yearly: 350, save: 70 },
    features: [
      { text: 'Latin Roots', subItems: ['Why study roots?'] },
      'Root Families',
      'Excerpt Reading',
      'Parts of Speech',
      'Synonyms',
      'Definitions',
      'Spelling',
      'Syllables',
      'Pronunciation',
      'Word Games',
      'Prep for: ISEE, CTP, PSAT, SAT, ACT, GRE',
      'Progress Reports',
      'Vocabulary from the Student’s Reading(s)'
    ],
    buttons: { 
      monthly: 'Order Plan 2 Monthly', 
      yearly: 'Order Plan 2 Year',
      monthlyLink: '/order/plan2/monthly',
      yearlyLink: '/order/plan2/yearly'
    }
  },
  {
    title: 'Study Plan 3',
    price: { monthly: 120, yearly: 1200, save: 240 },
    features: [
      { text: 'Latin Roots', subItems: ['Why study roots?'] },
      'Root Families',
      'Excerpt Reading',
      'Parts of Speech',
      'Synonyms',
      'Definitions',
      'Spelling',
      'Syllables',
      'Pronunciation',
      'Word Games',
      'Prep for: ISEE, CTP, PSAT, SAT, ACT, GRE',
      'Progress Reports',
      'Vocabulary from the Student’s Reading(s)',
      'Tutor-Guided Isam more Sentence Writing',
      'Tutor-guided Grammar Scope and Sequence',
      '2 Week Free Trial'
    ],
    buttons: { 
      monthly: 'Order Plan 3 Monthly', 
      yearly: 'Order Plan 3 Year',
      monthlyLink: '/order/plan3/monthly',
      yearlyLink: '/order/plan3/yearly'
    }
  },
  {
    title: 'Study Plan 4',
    price: { monthly: 200, yearly: 2000, save: 400 },
    features: [
      { text: 'Latin Roots', subItems: ['Why study roots?'] },
      'Root Families',
      'Excerpt Reading',
      'Parts of Speech',
      'Synonyms',
      'Definitions',
      'Spelling',
      'Syllables',
      'Pronunciation',
      'Word Games',
      'Prep for: ISEE, CTP, PSAT, SAT, ACT, GRE',
      'Progress Reports',
      'Vocabulary from the Student’s Reading(s)',
      'Tutor-Guided Isam more Sentence Writing',
      'Tutor-guided Grammar Scope and Sequence',
      '2 Week Free Trial',
      'Weekly Live Tutoring Session'
    ],
    buttons: { 
      monthly: 'Order Plan 4 Monthly', 
      yearly: 'Order Plan 4 Year',
      monthlyLink: '/order/plan4/monthly',
      yearlyLink: '/order/plan4/yearly'
    }
  }
];

const StudyPlansPage = () => {
  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        py: 4,
        px: 3,
        marginX: 'auto',
        maxWidth: { md: '90%', lg: '1200px' }
      }}
    >
      <Typography variant="h3" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
        Word Voyage Self-Study Plans
      </Typography>
      
      <Grid container spacing={4} justifyContent="center">
        {plans.map((plan, index) => (
          <Grid 
            item 
            xs={12} 
            sm={6} 
            md={4} 
            lg={3} 
            key={index} 
            sx={{ maxWidth: 350 }}
          >
            <Card 
              variant="outlined" 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                marginX: 'auto'
              }}
            >
              <CardHeader
                title={plan.title}
                titleTypographyProps={{ variant: 'h6' }}
                sx={{ bgcolor: 'background.default' }}
              />
              
              <CardContent sx={{ flexGrow: 1 }}>
                <List dense>
                  {plan.features.map((feature, idx) => {
                    const featureText = typeof feature === 'string' ? feature : feature.text;
                    const color = redFeatures.has(featureText) ? '#ff0000' : 
                                blueFeatures.has(featureText) ? '#1976d2' : 'inherit';

                    return typeof feature === 'string' ? (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { color }
                          }} 
                        />
                      </ListItem>
                    ) : (
                      <React.Fragment key={idx}>
                        <ListItem sx={{ py: 0.5 }}>
                          <ListItemText 
                            primary={feature.text} 
                            primaryTypographyProps={{ 
                              variant: 'body2', 
                              fontWeight: 500,
                              sx: { color }
                            }}
                          />
                        </ListItem>
                        {feature.subItems.map((subItem, subIdx) => (
                          <ListItem key={`${idx}-${subIdx}`} sx={{ py: 0.5, pl: 3 }}>
                            <ListItemText 
                              primary={subItem} 
                              primaryTypographyProps={{ 
                                variant: 'body2', 
                                color: 'text.secondary' 
                              }}
                            />
                          </ListItem>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </List>
              </CardContent>

              <Divider />
              
              <CardActions sx={{ flexDirection: 'column', p: 2, gap: 1 }}>
                <Box textAlign="center">
                  <Typography variant="h6" component="div">
                    ${plan.price.monthly}/month
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or ${plan.price.yearly}/year (save ${plan.price.save})
                  </Typography>
                </Box>
                
                {/* Bouton Mensuel */}
                <Button 
                  variant="contained"
                  fullWidth
                  size="small"
                  href={plan.buttons.monthlyLink}
                  sx={{ 
                    backgroundColor: '#366092',
                    color:'white',
                  }}
                >
                  {plan.buttons.monthly}
                </Button>

                {/* Bouton Annuel */}
                <Button 
                  variant="outlined"
                  fullWidth
                  size="small"
                  href={plan.buttons.yearlyLink}
                  sx={{ 
                    backgroundColor: '#80011F',
                    color:'white',
                  }}
                >
                  {plan.buttons.yearly}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default StudyPlansPage;